/* eslint-disable no-console */
import React from 'react';
import {
  Keyboard,
  Platform,
  ScrollView,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';

import {ExitPopup} from '/component/base/ExitPopup';
import ModalHeader from '/component/layout/ModalHeader';
import {ManageMySiteEditTestIDs} from '/constant/TestIDs';
import {usePopup} from '/provider/PopupProvider';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {useImagePicker} from '../hooks/useImagePicker';
import {ManageMySiteEditProps} from '../ManageMySite.types';
import {AboutSection} from '../sections/AboutSection';
import {AddressSection} from '../sections/AddressSection';
import {AvatarSection} from '../sections/AvatarSection';
import {ContactSection} from '../sections/ContactSection';
import {PhotosSection} from '../sections/PhotosSection';
import {WebsiteSection} from '../sections/WebsiteSection';
import {
  StickyActionFooter,
  useKeyboardVisible,
} from '../StickyActionFooter/StickyActionFooter';
import {ErrorBanner} from '../widgets/ErrorBanner/ErrorBanner';

import {useManageMySiteEdit} from './ManageMySiteEditScreen.hook';
import styles from './ManageMySiteEditScreen.style';

export const ManageMySiteEditScreen = (props: ManageMySiteEditProps) => {
  const {showPopup, hidePopup} = usePopup() as {
    showPopup: (...args: any[]) => void;
    hidePopup: () => void;
  };

  const {openPicker} = useImagePicker();
  const isKeyboardVisible = useKeyboardVisible();

  const {scrollRef, values, setters, derived, actions} =
    useManageMySiteEdit(props);

  const showErrorBanner = derived.hasErrors && derived.hasSubmitted;

  const isWeb = Platform.OS === 'web';

  const scrollToTop = () => {
    if (isWeb) {
      (scrollRef.current as any)?.scrollTo?.({x: 0, y: 0, animated: true});
      return;
    }

    if (Platform.OS === 'android') {
      const node =
        scrollRef.current?.getScrollResponder?.() as unknown as ScrollView | null;

      if (node) {
        node.scrollTo({x: 0, y: 0, animated: true});
      } else {
        scrollRef.current?.scrollToPosition?.(0, 0, true);
      }

      return;
    }

    scrollRef.current?.scrollToPosition?.(0, 0, true);
  };

  const avatarDisplayUrl = React.useMemo(() => {
    if (values.avatarPreview?.uri) {
      return values.avatarPreview.uri;
    }

    return values.avatar || '';
  }, [values.avatarPreview?.uri, values.avatar]);

  const handleSave = () => {
    if (derived.hasErrors) {
      actions.handleSave();

      if (!isWeb) {
        Keyboard.dismiss();
      }

      setTimeout(() => scrollToTop(), Platform.OS === 'android' ? 300 : 100);
      return;
    }

    actions.handleSave();
  };

  const handleCancelChanges = () => {
    if (!isWeb) {
      Keyboard.dismiss();
    }

    actions.resetUnsavedState();
  };

  const handleClose = () => {
    if (props.isSaving || derived.isAnyPhotoUploading) {
      return;
    }

    if (!derived.unsavedChanges) {
      props.onClose();
      return;
    }

    showPopup(
      ExitPopup,
      {
        title: 'Are you sure you want to go back?',
        description: 'All unsaved changes will be lost.',
        alignButtonsAsColumn: true,
        handleCancelButton: hidePopup,
        handleDiscardButton: async () => {
          await hidePopup();
          actions.resetUnsavedState();
          props.onClose();
        },
        exitButton: hidePopup,
        discardButtonStyle: {backgroundColor: '#000'},
      },
      {avoidable: false, showFullModal: false},
    );
  };

  const outerBottomPadding =
    derived.formDirty || isKeyboardVisible
      ? Platform.OS === 'android'
        ? 1
        : 15
      : Platform.OS === 'android'
      ? 96
      : 48;

  const scrollBottomPadding =
    isKeyboardVisible || (derived.formDirty && Platform.OS === 'android')
      ? 50
      : 10;

  const ScrollComponent = isWeb ? ScrollView : KeyboardAwareScrollView;

  const content = (
    <View>
      {showErrorBanner && (
        <ErrorBanner
          testID={ManageMySiteEditTestIDs.errorBanner.container}
          errorCount={derived.errorCount}
          hasSubmitted={showErrorBanner}
        />
      )}

      <AvatarSection
        avatarUrl={avatarDisplayUrl}
        previewUrl={values.avatarPreview?.uri || ''}
        onChangePhoto={imageData => {
          if (imageData) {
            actions.handleAvatarPicked(imageData);
          } else {
            openPicker('avatar', image => {
              actions.handleAvatarPicked(image);
            });
          }
        }}
        onConfirmPhoto={async () => {
          try {
            await actions.handleAvatarConfirm();
          } catch (error) {
            console.error(
              '[ON CONFIRM PHOTO] Avatar confirmation failed:',
              error,
            );
          }
        }}
        onCancelPhoto={actions.handleAvatarCancel}
        onRetryPhoto={actions.handleRetryAvatarUpload}
        fullName={values.name}
        isUploading={derived.isAvatarUploading}
        avatarStatus={derived.avatarStatus}
      />

      <WebsiteSection
        testID={ManageMySiteEditTestIDs.sections.website}
        value={values.slug}
        onChange={setters.setSlug}
        onBlur={() => setters.markFieldTouched('slug')}
        error={derived.validationErrors.slug ?? ''}
        hasSubmitted={derived.hasSubmitted || derived.touchedFields.slug}
        baseWebsiteUrl={derived.resolvedBaseWebsiteUrl}
      />

      <ContactSection
        testID={ManageMySiteEditTestIDs.sections.contact}
        name={values.name}
        mail={values.mail}
        primary={values.primary}
        secondary={values.secondary}
        onNameChange={setters.setName}
        onMailChange={setters.setMail}
        onPrimaryChange={setters.setPrimary}
        onSecondaryChange={setters.setSecondary}
        onNameBlur={() => setters.markFieldTouched('name')}
        onMailBlur={() => setters.markFieldTouched('mail')}
        onPrimaryBlur={() => setters.markFieldTouched('primary')}
        onSecondaryBlur={() => setters.markFieldTouched('secondary')}
        hasSubmitted={derived.hasSubmitted}
        touchedFields={derived.touchedFields}
        errors={derived.validationErrors}
      />

      <AddressSection
        testID={ManageMySiteEditTestIDs.sections.address}
        street={values.streetVal}
        city={values.cityVal}
        state={values.stateVal}
        zip={values.zip}
        initialCountry={values.country}
        country={values.countryVal}
        onStreetChange={setters.setStreetVal}
        onCityChange={setters.setCityVal}
        onCountryChange={setters.setCountryVal}
        onStateChange={setters.setStateVal}
        onZipChange={setters.setZip}
        onZipBlur={() => setters.markFieldTouched('zip')}
        hasSubmitted={derived.hasSubmitted || derived.touchedFields.zip}
        errors={derived.validationErrors}
      />

      <AboutSection
        testID={ManageMySiteEditTestIDs.sections.about}
        value={values.aboutVal}
        onChange={setters.setAboutVal}
        onBlur={() => setters.markFieldTouched('about')}
        hasSubmitted={derived.hasSubmitted || derived.touchedFields.about}
        error={derived.validationErrors.about ?? ''}
      />

      <PhotosSection
        testID={ManageMySiteEditTestIDs.sections.photos}
        beforeUrl={
          props.hasBeforeImage || values.before ? values.before || '' : ''
        }
        afterUrl={props.hasAfterImage || values.after ? values.after || '' : ''}
        onUpload={actions.handlePhotoUpload}
        openPicker={openPicker}
        onDirtyStateChange={setters.setHasPendingPhotoPreview}
        beforeUploadSuccess={derived.beforeUploadSuccess}
        afterUploadSuccess={derived.afterUploadSuccess}
        beforeUploadError={derived.beforeUploadError}
        afterUploadError={derived.afterUploadError}
        onRetryBeforeUpload={actions.handleRetryBeforeUpload}
        onRetryAfterUpload={actions.handleRetryAfterUpload}
        onCancelUpload={actions.handleTransformationPhotoCancel}
      />
    </View>
  );

  return (
    <ModalHeader
      title={tt('manageMySite', 'Manage My Site')}
      onClose={handleClose}
      accessibilityLabel="Manage My Site"
      accessibilityHint="Navigates to Manage My Site view screen"
      closeIconName="chevron-left"
      style={{
        base: styles.base,
        headerText: styles.headerText,
        actionIcon: styles.goBackIcon,
      }}>
      <View
        style={[styles.scrollViewBase, {paddingBottom: outerBottomPadding}]}
        pointerEvents={
          props.isSaving || derived.isAnyPhotoUploading ? 'none' : 'auto'
        }>
        <ScrollComponent
          ref={scrollRef}
          testID={ManageMySiteEditTestIDs.scrollView}
          style={styles.scrollViewBase}
          contentContainerStyle={[
            styles.contentWithFooter,
            {paddingBottom: scrollBottomPadding},
          ]}
          enableResetScrollToCoords={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={isWeb ? 'none' : 'on-drag'}
          showsVerticalScrollIndicator={false}
          enableOnAndroid
          enableAutomaticScroll
          extraScrollHeight={Platform.OS === 'android' ? 150 : 80}
          extraHeight={Platform.OS === 'android' ? 150 : 80}
          nestedScrollEnabled
          scrollEventThrottle={16}
          bounces
          alwaysBounceVertical={false}>
          {isWeb ? (
            content
          ) : (
            <TouchableWithoutFeedback
              onPress={Keyboard.dismiss}
              accessible={false}
              focusable={false}
              importantForAccessibility="no">
              {content}
            </TouchableWithoutFeedback>
          )}
        </ScrollComponent>

        {(props.isSaving || derived.isAnyPhotoUploading) && (
          <View
            testID={ManageMySiteEditTestIDs.overlay}
            style={styles.containerOverlay}
            pointerEvents="box-only"
            accessible
            accessibilityRole="none"
            accessibilityLabel="Saving in progress, please wait"
            accessibilityLiveRegion="polite"
          />
        )}

        <StickyActionFooter
          onCancel={
            props.isSaving || derived.isAnyPhotoUploading
              ? () => {}
              : handleCancelChanges
          }
          onSave={handleSave}
          isSaving={props.isSaving}
          showSave={derived.formDirty}
          showCancel={derived.formDirty}
          disableSave={props.isSaving || derived.isAnyPhotoUploading}
          cancelTestID={ManageMySiteEditTestIDs.footer.cancelButton}
          saveTestID={ManageMySiteEditTestIDs.footer.saveButton}
        />
      </View>
    </ModalHeader>
  );
};

export default ManageMySiteEditScreen;
